import React, { useEffect, useState } from 'react';
import { Card } from '../../components/base/Card';
import { Button } from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { SEO, getOrganizationSchema } from '../../utils/seo';
import { supabase } from '../../lib/supabase';

export const AboutPage: React.FC = () => {
  const [pageData, setPageData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('slug', 'hakkimizda')
        .eq('is_published', true)
        .single();

      if (error) throw error;
      if (data) {
        setPageData(data);
      }
    } catch (error) {
      console.error('Sayfa yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C1121F]"></div>
      </div>
    );
  }

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Kalite Odaklılık',
      description: 'Her üretim aşamasında titiz kalite kontrol süreçleri uyguluyoruz.'
    },
    {
      icon: 'ri-time-line',
      title: 'Zamanında Teslimat',
      description: 'Belirlenen sürelerde eksiksiz ve hatasız teslimat garantisi veriyoruz.'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Müşteri Memnuniyeti',
      description: 'Müşteri ihtiyaçlarını anlayarak en uygun çözümleri sunuyoruz.'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'İnovasyon',
      description: 'Sürekli gelişen teknoloji ile üretim süreçlerimizi yeniliyoruz.'
    }
  ];

  const team = [
    {
      name: 'Mehmet Yılmaz',
      position: 'Genel Müdür & CNC Uzmanı',
      experience: '15 yıl deneyim',
      image: 'https://public.readdy.ai/ai/img_res/9904d7d7540593588c23392f147b09bd.jpg'
    },
    {
      name: 'Ahmet Kaya',
      position: 'Kaynak Uzmanı',
      experience: '12 yıl deneyim',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20welder%20in%20safety%20gear%2C%20experienced%20craftsman%20with%20welding%20mask%2C%20red%20and%20white%20industrial%20workshop%20background&width=300&height=300&seq=team-ahmet&orientation=squarish'
    },
    {
      name: 'Fatma Demir',
      position: 'Kalite Kontrol Uzmanı',
      experience: '8 yıl deneyim',
      image: 'https://readdy.ai/api/search-image?query=Professional%20Turkish%20female%20quality%20control%20engineer%20with%20measuring%20instruments%2C%20confident%20woman%20in%20industrial%20setting%20with%20red%20accents&width=300&height=300&seq=team-fatma&orientation=squarish'
    },
    {
      name: 'Oğuz Çelik',
      position: '3D Tasarım & Baskı Uzmanı',
      experience: '6 yıl deneyim',
      image: 'https://readdy.ai/api/search-image?query=Young%20Turkish%203D%20printing%20specialist%20working%20with%20modern%203D%20printers%2C%20tech-savvy%20professional%20in%20clean%20workshop%20environment%20with%20red%20lighting&width=300&height=300&seq=team-oguz&orientation=squarish'
    }
  ];

  const milestones = [
    { year: '2014', event: 'Şirket kuruluşu ve ilk CNC torna yatırımı' },
    { year: '2016', event: 'CNC dik işleme merkezi ve kaynak bölümü eklendi' },
    { year: '2018', event: 'ISO 9001 kalite belgesi alındı' },
    { year: '2020', event: '3D printer atölyesi kuruldu' },
    { year: '2022', event: 'Yeni nesil CNC makineler ile kapasite artırıldı' },
    { year: '2024', event: '500+ başarılı proje tamamlandı' }
  ];

  const aboutSchema = {
    "@context": "https://schema.org",
    "@graph": [
      getOrganizationSchema(),
      {
        "@type": "AboutPage",
        "name": "Hakkımızda - 3DP Makina",
        "description": "2014 yılından bu yana hassas imalat sektöründe güvenilir çözüm ortağınız. Kalite, zamanında teslimat ve müşteri memnuniyeti odaklı hizmet anlayışımız.",
        "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/hakkimizda`,
        "mainEntity": {
          "@type": "Organization",
          "name": "3DP Makina",
          "foundingDate": "2014",
          "numberOfEmployees": "10-20"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Hakkımızda | 3DP Makina - Hassas İmalat Uzmanları"
        description="2014 yılından bu yana hassas imalat sektöründe güvenilir çözüm ortağınız. Kalite, zamanında teslimat ve müşteri memnuniyeti odaklı hizmet anlayışımız."
        keywords="hakkımızda, 3DP Makina, hassas imalat, CNC uzmanları, kalite kontrol, deneyimli ekip"
        canonical={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/hakkimizda`}
        ogTitle="Hakkımızda | 3DP Makina - Hassas İmalat Uzmanları"
        ogDescription="2014 yılından bu yana hassas imalat sektöründe güvenilir çözüm ortağınız."
        ogUrl={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/hakkimizda`}
        jsonLd={aboutSchema}
      />

      {/* Hero Section */}
      <section 
        className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(22, 22, 22, 0.7), rgba(22, 22, 22, 0.7)), url('https://readdy.ai/api/search-image?query=Modern%20Turkish%20manufacturing%20workshop%20with%20team%20of%20skilled%20workers%2C%20CNC%20machines%20and%20welding%20equipment%2C%20red%20and%20white%20industrial%20lighting%2C%20professional%20atmosphere&width=1920&height=600&seq=about-hero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl font-bold mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Hakkımızda
            </h1>
            <p className="text-xl leading-relaxed">
              2014 yılından bu yana hassas imalat sektöründe güvenilir çözüm ortağınız. 
              Kalite, zamanında teslimat ve müşteri memnuniyeti odaklı hizmet anlayışımızla 
              sektörde öncü konumdayız.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#161616] mb-8" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Misyonumuz
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Modern teknoloji ve uzman kadromuzla müşterilerimizin imalat ihtiyaçlarına 
                en kaliteli, hızlı ve ekonomik çözümleri sunmak. Her projede mükemmellik 
                standardını yakalamak ve sürdürülebilir üretim anlayışıyla sektöre değer katmak.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Tutarlılık, şeffaflık ve sürekli gelişim ilkelerimizle müşteri memnuniyetini 
                en üst düzeyde tutarak, uzun vadeli iş ortaklıkları kurmayı hedefliyoruz.
              </p>
            </div>
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20CNC%20machining%20center%20in%20operation%20with%20precision%20metal%20cutting%2C%20red%20and%20white%20industrial%20lighting%2C%20clean%20organized%20workshop%20environment&width=600&height=400&seq=mission-image&orientation=landscape"
                alt="Misyonumuz"
                className="w-full h-80 object-cover object-top rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Değerlerimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              İş yapış şeklimizi belirleyen temel değerlerimiz, her projede rehberimizdir.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} hover className="text-center">
                <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#161616] mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Uzman Ekibimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alanında uzman, deneyimli kadromuzla her projede en iyi sonucu elde ediyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <Card key={index} hover className="text-center">
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-32 h-32 rounded-full mx-auto mb-6 object-cover object-top"
                />
                <h3 className="text-xl font-semibold text-[#161616] mb-2">
                  {member.name}
                </h3>
                <p className="text-[#C1121F] font-medium mb-2">
                  {member.position}
                </p>
                <p className="text-gray-600 text-sm">
                  {member.experience}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Tarihçemiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              10 yılı aşkın süredir sürdürdüğümüz büyüme ve gelişim hikayemiz.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#C1121F]"></div>
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <Card className="inline-block">
                      <div className="text-2xl font-bold text-[#C1121F] mb-2">
                        {milestone.year}
                      </div>
                      <p className="text-gray-600">
                        {milestone.event}
                      </p>
                    </Card>
                  </div>
                  <div className="w-6 h-6 bg-[#C1121F] rounded-full border-4 border-white shadow-lg relative z-10"></div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Workshop Gallery */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Atölyemiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Modern ekipmanlar ve düzenli çalışma alanlarımızla kaliteli üretim gerçekleştiriyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'CNC Torna Bölümü',
                image: 'https://readdy.ai/api/search-image?query=Modern%20CNC%20lathe%20machines%20in%20Turkish%20workshop%2C%20red%20and%20white%20industrial%20lighting%2C%20organized%20metalworking%20station%20with%20precision%20tools&width=400&height=300&seq=workshop-cnc&orientation=landscape'
              },
              {
                title: 'Freze İşleme Merkezi',
                image: 'https://readdy.ai/api/search-image?query=CNC%20milling%20center%20with%20vertical%20machining%20capabilities%2C%20red%20accent%20lighting%2C%20clean%20industrial%20environment%20with%20metal%20chips&width=400&height=300&seq=workshop-milling&orientation=landscape'
              },
              {
                title: 'Kaynak Atölyesi',
                image: 'https://readdy.ai/api/search-image?query=Professional%20welding%20station%20with%20TIG%20and%20MIG%20equipment%2C%20red%20and%20white%20workshop%20theme%2C%20organized%20welding%20area%20with%20safety%20equipment&width=400&height=300&seq=workshop-welding&orientation=landscape'
              },
              {
                title: '3D Printer Bölümü',
                image: 'https://readdy.ai/api/search-image?query=Modern%203D%20printing%20facility%20with%20multiple%20FDM%20printers%2C%20red%20accent%20lighting%2C%20clean%20prototyping%20workspace%20with%20printed%20parts%20display&width=400&height=300&seq=workshop-3d&orientation=landscape'
              },
              {
                title: 'Kalite Kontrol',
                image: 'https://readdy.ai/api/search-image?query=Quality%20control%20station%20with%20CMM%20coordinate%20measuring%20machine%2C%20precision%20measurement%20tools%2C%20red%20and%20white%20industrial%20lighting&width=400&height=300&seq=workshop-quality&orientation=landscape'
              },
              {
                title: 'Montaj Alanı',
                image: 'https://readdy.ai/api/search-image?query=Assembly%20area%20with%20workbenches%20and%20precision%20tools%2C%20organized%20Turkish%20workshop%20with%20red%20and%20white%20theme%2C%20clean%20industrial%20environment&width=400&height=300&seq=workshop-assembly&orientation=landscape'
              }
            ].map((gallery, index) => (
              <Card key={index} hover className="overflow-hidden">
                <img 
                  src={gallery.image}
                  alt={gallery.title}
                  className="w-full h-48 object-cover object-top"
                />
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-[#161616]">
                    {gallery.title}
                  </h3>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C1121F]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Bizimle Çalışmaya Hazır mısınız?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Deneyimli ekibimiz ve modern ekipmanlarımızla projenizi hayata geçirelim.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/teklif-al">
              <Button variant="secondary" size="lg">
                <i className="ri-file-upload-line mr-2"></i>
                Hemen Teklif Al
              </Button>
            </Link>
            <Link to="/projeler">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#C1121F]">
                <i className="ri-eye-line mr-2"></i>
                Projelerimizi İncele
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
